/**
 * Knowledge Health — the "Datadog for knowledge" dashboard.
 *
 * Renders the live composite snapshot from
 * `GET /api/v1/governance/knowledge-health/?workspace=<id>`
 * (apps.governance.services.knowledge_health): an A–F knowledge-debt grade,
 * per-dimension scores (coverage / freshness / consistency / confidence),
 * the raw metrics behind them, and recommended actions derived from the gaps.
 *
 * Business framing: this is how an operator trusts the knowledge base at a
 * glance and how a CDO tracks knowledge debt quarter over quarter.
 */
import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Activity, AlertTriangle, ArrowRight, CheckCircle2, Database, GitMerge,
  Hexagon, Loader2, ShieldCheck, Sparkles, Unplug,
} from "lucide-react";
import { rawFetch } from "@/lib/api/client";
import { PageHeader, ErrorBox, EmptyState } from "@/components/common";
import { useApp } from "@/lib/store";

interface Health {
  score: number;
  grade: "A" | "B" | "C" | "D" | "F";
  dimensions: {
    coverage: number;
    freshness: number;
    consistency: number;
    confidence: number;
  };
  metrics: {
    ontology_published: boolean;
    entity_types: number;
    relation_types: number;
    nodes: number;
    typed_nodes: number;
    typed_share: number;
    event_volume: number;
    freshness_seconds: number | null;
    open_violations: number;
    duplicate_backlog: number;
    confidence_scored: number;
    low_confidence: number;
    orphan_nodes: number;
    orphan_share: number;
  };
}

const GRADE_CLS: Record<Health["grade"], string> = {
  A: "text-good",
  B: "text-good",
  C: "text-accent",
  D: "text-bad",
  F: "text-bad",
};

function scoreClass(v: number): string {
  if (v >= 0.75) return "text-good";
  if (v >= 0.5) return "text-accent";
  return "text-bad";
}
function barClass(v: number): string {
  if (v >= 0.75) return "bg-good";
  if (v >= 0.5) return "bg-accent";
  return "bg-bad";
}
function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}
function humanizeAge(seconds: number | null): string {
  if (seconds == null) return "no events yet";
  const d = seconds / 86400;
  if (d >= 1) return `${d.toFixed(1)} d ago`;
  const h = seconds / 3600;
  if (h >= 1) return `${h.toFixed(1)} h ago`;
  const m = seconds / 60;
  return `${Math.max(1, Math.round(m))} min ago`;
}

export function KnowledgeHealth() {
  const ws = useApp((s) => s.workspaceId);

  const q = useQuery<Health>({
    queryKey: ["knowledge-health", ws],
    queryFn: () => rawFetch<Health>(`/governance/knowledge-health/?workspace=${ws}`),
    enabled: !!ws,
  });

  const actions = useMemo(() => (q.data ? recommend(q.data) : []), [q.data]);

  if (!ws) {
    return (
      <>
        <PageHeader title="Knowledge Health"
                    subtitle="Coverage, freshness, consistency and confidence — scored into a knowledge-debt grade." />
        <EmptyState title="Select a workspace" hint="Knowledge Health is scored per workspace. Pick one from the header." />
      </>
    );
  }
  if (q.isLoading) {
    return (
      <div className="card flex items-center gap-2 text-sm text-muted">
        <Loader2 size={14} className="animate-spin" /> Scoring knowledge health…
      </div>
    );
  }
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data) return null;

  const h = q.data;
  const m = h.metrics;

  return (
    <>
      <PageHeader
        title="Knowledge Health"
        subtitle="A knowledge-debt grade for this workspace."
        help={
          <>
            <p>Scores four dimensions — <strong>coverage, freshness, consistency, confidence</strong> — into an A–F grade, so you can trust the knowledge base at a glance. Recommended actions deep-link to where you fix each gap.</p>
          </>
        }
        learnMore={{ to: "/howto", hash: "governance", label: "About governance" }}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        {/* Overall grade */}
        <div className="card flex flex-col items-center justify-center text-center">
          <div className="text-xs uppercase tracking-wide text-muted">Knowledge debt grade</div>
          <div className={`my-1 text-6xl font-bold ${GRADE_CLS[h.grade]}`}>{h.grade}</div>
          <div className={`text-2xl font-semibold ${scoreClass(h.score)}`}>{pct(h.score)}</div>
          <div className="mt-2 text-xs text-muted">
            {h.grade === "A" || h.grade === "B"
              ? "Healthy — keep ingesting and reviewing."
              : h.grade === "C"
                ? "Workable — close the gaps below."
                : "Needs attention — see recommended actions."}
          </div>
        </div>

        {/* Dimensions */}
        <div className="card">
          <div className="mb-3 text-sm font-medium">Dimensions</div>
          <div className="space-y-3">
            <DimBar Icon={Hexagon} label="Coverage"
                    hint="Published ontology + share of typed graph nodes" value={h.dimensions.coverage} />
            <DimBar Icon={Activity} label="Freshness"
                    hint="Age of the most recent behavioural event" value={h.dimensions.freshness} />
            <DimBar Icon={ShieldCheck} label="Consistency"
                    hint="Inverse of open violations + duplicate-merge backlog" value={h.dimensions.consistency} />
            <DimBar Icon={CheckCircle2} label="Confidence"
                    hint="Share of answers/extractions above the low-confidence line" value={h.dimensions.confidence} />
          </div>
        </div>
      </div>

      {/* Metrics */}
      <div className="mt-4">
        <div className="mb-2 text-sm font-medium">Metrics</div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
          <Metric Icon={Hexagon} label="Entity types" value={m.entity_types}
                  sub={m.ontology_published ? "published ontology" : "no published ontology"} />
          <Metric Icon={Hexagon} label="Relation types" value={m.relation_types} />
          <Metric Icon={Database} label="Graph nodes" value={m.nodes} />
          <Metric Icon={Database} label="Typed nodes" value={`${m.typed_nodes} (${pct(m.typed_share)})`} />
          <Metric Icon={Unplug} label="Orphan nodes" value={`${m.orphan_nodes} (${pct(m.orphan_share)})`}
                  warn={m.orphan_nodes > 0} />
          <Metric Icon={GitMerge} label="Duplicate backlog" value={m.duplicate_backlog}
                  warn={m.duplicate_backlog > 0} />
          <Metric Icon={AlertTriangle} label="Open violations" value={m.open_violations}
                  warn={m.open_violations > 0} />
          <Metric Icon={CheckCircle2} label="Low-confidence" value={`${m.low_confidence} / ${m.confidence_scored}`}
                  warn={m.low_confidence > 0} />
          <Metric Icon={Activity} label="Event volume" value={m.event_volume} />
          <Metric Icon={Activity} label="Freshness" value={humanizeAge(m.freshness_seconds)} />
        </div>
      </div>

      {/* Recommended actions */}
      <div className="card mt-4">
        <div className="mb-2 flex items-center gap-2 text-sm font-medium">
          <Sparkles size={16} className="text-accent" /> Recommended actions
        </div>
        {actions.length === 0 ? (
          <p className="text-sm text-muted">
            No action needed right now — coverage, freshness, consistency and confidence are all healthy.
          </p>
        ) : (
          <ul className="space-y-2">
            {actions.map((a) => (
              <li key={a.label} className="flex items-start gap-2 text-sm">
                <ArrowRight size={14} className="mt-0.5 shrink-0 text-accent" />
                <span>
                  <Link to={a.to} className="text-accent underline">{a.label}</Link>
                  <span className="text-muted"> — {a.why}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="mt-3 text-xs text-muted">
        Scored live from <code>GET /api/v1/governance/knowledge-health/</code>. Grade reflects this
        workspace only.
      </p>
    </>
  );
}

function DimBar({
  Icon, label, hint, value,
}: { Icon: typeof Hexagon; label: string; hint: string; value: number }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="flex items-center gap-1.5">
          <Icon size={14} className="text-muted" /> {label}
        </span>
        <span className={`font-medium ${scoreClass(value)}`}>{pct(value)}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-border/40">
        <div className={`h-full rounded-full ${barClass(value)}`} style={{ width: pct(value) }} />
      </div>
      <div className="mt-0.5 text-xs text-muted">{hint}</div>
    </div>
  );
}

function Metric({
  Icon, label, value, sub, warn,
}: {
  Icon: typeof Hexagon; label: string; value: string | number; sub?: string; warn?: boolean;
}) {
  return (
    <div className="card">
      <div className="flex items-center gap-1.5 text-xs text-muted">
        <Icon size={13} className={warn ? "text-bad" : "text-muted"} /> {label}
      </div>
      <div className={`mt-1 text-lg font-semibold ${warn ? "text-bad" : ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted">{sub}</div>}
    </div>
  );
}

/** Derive concrete next steps from the metrics — the "Recommended Actions" panel. */
function recommend(h: Health): Array<{ label: string; why: string; to: string }> {
  const out: Array<{ label: string; why: string; to: string }> = [];
  const m = h.metrics;
  if (!m.ontology_published) {
    out.push({ label: "Publish an ontology version", why: "no published ontology — extraction has no contract", to: "/ontology" });
  }
  if (m.duplicate_backlog > 0) {
    out.push({ label: "Review merge proposals", why: `${m.duplicate_backlog} duplicate-merge proposal(s) awaiting approval`, to: "/governance" });
  }
  if (m.open_violations > 0) {
    out.push({ label: "Resolve shape violations", why: `${m.open_violations} open SHACL violation(s)`, to: "/governance" });
  }
  if (m.orphan_nodes > 0) {
    out.push({ label: "Connect orphan entities", why: `${m.orphan_nodes} node(s) have no relationships`, to: "/pipelines" });
  }
  if (m.low_confidence > 0) {
    out.push({ label: "Inspect low-confidence answers", why: `${m.low_confidence} answer(s)/extraction(s) below the confidence line`, to: "/governance" });
  }
  if (h.dimensions.freshness < 0.5) {
    out.push({ label: "Run a pipeline to refresh", why: "the most recent event is stale", to: "/pipelines" });
  }
  if (m.nodes > 0 && m.typed_share < 0.9) {
    out.push({ label: "Improve entity typing", why: `only ${pct(m.typed_share)} of nodes carry an entity type`, to: "/ontology" });
  }
  return out;
}
